import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { PaymentModalPriceDetails } from '@/components/ClientTransactionDetails';
import Button from '@/design_system/Button';
import Dialog from '@/design_system/Dialog';
import { Footer, FooterButtons, FooterError } from '@/design_system/Footer';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import { getRefashionStatus } from '@/models/actionType';
import { useMedia } from '@/models/medium';
import {
  RequestWithRelations,
  useClientAmountDue,
  useUpdateRequestExternalPaymentReference,
  useValidateExternalPayment,
  useValidateExternalRefund,
} from '@/models/request';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';

import { isPaymentReferenceInvalid, PaymentReferenceInput } from './PaymentReferenceInput';
import { isProofOfPaymentInvalid, ProofOfPaymentInput } from './ProofOfPaymentInput';

export const ExternalPaymentOrRefundModal = ({
  step,
  onActionDone,
  onClose,
}: {
  step: 'payment' | 'refund';
  onActionDone: () => void;
  onClose: () => void;
}) => {
  const { t } = useLingui();
  const { request } = useRequestContext();

  const stepConfig = getPaymentOrRefundStepConfig(step, request);

  if (!stepConfig) {
    return null;
  }

  const title =
    step === 'payment'
      ? t({
          id: 'confirm-client-payment-or-refund-modal.payment.title',
          message: 'Confirm client payment',
        })
      : t({
          id: 'confirm-client-payment-or-refund-modal.refund.title',
          message: 'Confirm client refund',
        });

  return (
    <Dialog isOpen onOpenChange={onClose} title={title}>
      <ExternalPaymentOrRefundModalContent
        stepConfig={stepConfig}
        onActionDone={onActionDone}
        onClose={onClose}
        step={step}
      />
    </Dialog>
  );
};

const ExternalPaymentOrRefundModalContent = ({
  stepConfig,
  onActionDone,
  onClose,
  step,
}: {
  stepConfig: PaymentOrRefundStepConfig;
  onActionDone: () => void;
  onClose: () => void;
  step: 'payment' | 'refund';
}) => {
  const { t } = useLingui();
  const { request } = useRequestContext();
  const { data: clientAmountDue } = useClientAmountDue({ requestId: request.id });

  const {
    mutateAsync: updateRequestExternalPaymentReference,
    isPending: isUpdateRequestExternalPaymentReferencePending,
  } = useUpdateRequestExternalPaymentReference(request.id);
  const { mutateAsync: validateExternalPayment, isPending: isValidateExternalPaymentPending } =
    useValidateExternalPayment();
  const { mutateAsync: validateExternalRefund, isPending: isValidateExternalRefundPending } =
    useValidateExternalRefund();
  const isPending =
    isUpdateRequestExternalPaymentReferencePending ||
    isValidateExternalPaymentPending ||
    isValidateExternalRefundPending;

  const { data: { media: proofOfPaymentMedia } = { media: [] } } = useMedia({
    requestId: request.id,
    types: ['proof-of-external-payment'],
  });
  const [paymentReference, setPaymentReference] = useState<string>('');
  const [showErrors, setShowErrors] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const hasRefashionActions = request.articles.some((article) =>
    article.snapshot.articleActions.some(
      (action) => getRefashionStatus(action.organizationPrice) === 'applied'
    )
  );

  const handleConfirm = async () => {
    setShowErrors(true);

    const hasError =
      isPaymentReferenceInvalid({ paymentReference, stepConfig, hasRefashionActions, step }) ||
      isProofOfPaymentInvalid({ proofOfPaymentMedia, stepConfig, hasRefashionActions, step });

    if (hasError) {
      return;
    }

    try {
      if (paymentReference) {
        await updateRequestExternalPaymentReference({ externalPaymentReference: paymentReference });
      }

      if (step === 'payment') {
        await validateExternalPayment(request.id);
      } else {
        await validateExternalRefund(request.id);
      }

      onActionDone();
    } catch (err: any) {
      console.error(err);
      setApiError(
        (err.message as string) ?? t({ id: '_general.error.unknown', message: 'Unknown error' })
      );
    }
  };

  const description =
    step === 'payment'
      ? t({
          id: 'confirm-client-payment-or-refund-modal.payment.subtitle',
          message: 'Please provide the payment details required to complete this step.',
        })
      : t({
          id: 'confirm-client-payment-or-refund-modal.refund.subtitle',
          message: 'Please provide the refund details required to complete this step.',
        });

  return (
    <>
      <main>
        <Stack gap="1rem">
          <Paragraph>{description}</Paragraph>

          <PaymentModalPriceDetails prices={clientAmountDue?.prices ?? []} />

          <Stack gap="1rem">
            <PaymentReferenceInput
              paymentReference={paymentReference}
              setPaymentReference={setPaymentReference}
              stepConfig={stepConfig}
              hasRefashionActions={hasRefashionActions}
              showErrors={showErrors}
              step={step}
            />
            <ProofOfPaymentInput
              proofOfPaymentMedia={proofOfPaymentMedia}
              stepConfig={stepConfig}
              hasRefashionActions={hasRefashionActions}
              showErrors={showErrors}
              step={step}
            />
          </Stack>
        </Stack>
      </main>

      <Footer>
        {apiError && <FooterError>{apiError}</FooterError>}
        <FooterButtons>
          <Button variant="secondary" onPress={onClose} disabled={isPending}>
            <Trans id="confirm-client-payment-or-refund-modal.buttons.cancel">Cancel</Trans>
          </Button>
          <Button variant="primary" onPress={handleConfirm} isLoading={isPending}>
            {step === 'payment' ? (
              <Trans id="confirm-client-payment-or-refund-modal.payment.buttons.confirm">
                Confirm payment
              </Trans>
            ) : (
              <Trans id="confirm-client-payment-or-refund-modal.refund.buttons.confirm">
                Confirm refund
              </Trans>
            )}
          </Button>
        </FooterButtons>
      </Footer>
    </>
  );
};

export type PaymentOrRefundStepConfig = {
  fields?: {
    receipt?: { enabled: boolean; required: boolean };
    reference?: { enabled: boolean; required: boolean; format?: string };
  };
};

const getPaymentOrRefundStepConfig = (
  step: 'payment' | 'refund',
  request: RequestWithRelations
): PaymentOrRefundStepConfig | undefined => {
  if (step === 'payment') {
    return request.articles.find((article) => article.step?.step === 'payment')?.step
      ?.config as PaymentOrRefundStepConfig;
  } else {
    return request.articles.find((article) => article.step?.step === 'refund')?.step
      ?.config as PaymentOrRefundStepConfig;
  }
};
